import { useCallback } from 'preact/hooks';
import { DataState, ItemActionDetail, ListItemContext, Metadata } from '../../UNSAFE_Collection';

import { keyExtractor } from '../../utils/PRIVATE_collectionUtils';

export const useItemAction = <K extends string | number, D>(
  currentKey: K | undefined,
  dataState: DataState<K, D> | null,
  onItemAction: ((detail: ItemActionDetail<K, D>) => void) | undefined,
  itemSelector: string,
  allowSpacebar: boolean
) => {
  const onClick = useCallback(
    (event: MouseEvent) => {
      const key = keyExtractor(event.target as HTMLElement, itemSelector) as K;
      if (
        handleItemAction(
          key,
          dataState as DataState<K, D>,
          onItemAction as (detail: ItemActionDetail<K, D>) => void
        )
      ) {
        event.stopPropagation();
      }
    },
    [dataState, onItemAction, itemSelector]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleItemAction(
          currentKey,
          dataState as DataState<K, D>,
          onItemAction as (detail: ItemActionDetail<K, D>) => void
        );
      } else if (allowSpacebar && event.key === ' ') {
        handleItemAction(
          currentKey,
          dataState as DataState<K, D>,
          onItemAction as (detail: ItemActionDetail<K, D>) => void
        );
        event.preventDefault();
      }
    },
    [dataState, onItemAction, currentKey, allowSpacebar]
  );

  return !dataState || !onItemAction
    ? {}
    : {
        onClick,
        onKeyDown
      };
};

const handleItemAction = <K, D>(
  key: K | undefined,
  dataState: DataState<K, D>,
  onItemAction: (detail: ItemActionDetail<K, D>) => void
) => {
  if (key !== undefined) {
    const context = findItemContext(key, dataState);
    context && onItemAction({ context });
    return context !== undefined;
  }
  return false;
};

// returns a ListItemContext based on key
const findItemContext = <K, D>(
  key: K,
  dataState: DataState<K, D>
): ListItemContext<K, D> | undefined => {
  const index = dataState?.data?.findIndex((d) => d.metadata.key === key) ?? -1;
  if (index > -1) {
    const context: ListItemContext<K, D> = {
      index: index + (dataState?.offset || 0),
      data: dataState?.data[index].data as D,
      metadata: dataState?.data[index].metadata as Metadata<K>
    };
    return context;
  }
  return undefined;
};
